import { useEffect, useMemo, useState } from "react";
import Avatar from "@mui/material/Avatar";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";

import WaveEmojiIcon from "./WaveEmojiIcon";
import ChatbotDefaultIcon from "./ChatbotDefaultIcon";
import Suggestions from "./Suggestions";
import SendIcon from "./SendIcon";
import ChatbotIcon from "../../ChatbotIcon";

import CircleIcon from "@mui/icons-material/Circle";
import SvgIcon from "@mui/material/SvgIcon";

const loadingPercentage = ["0.5", "0.3", "0.2", "0.2"];

const ChatbotBody = ({
  chatbotSettings,
  chatbotAppearance,
}: {
  chatbotSettings: any;
  chatbotAppearance: any;
}) => {
  const userFontSettings = {
    color: chatbotAppearance?.["cuvo-chatbot-user-text-color"],
    fontFamily: chatbotAppearance?.["cuvo-chatbot-user-text-font-family"],
    fontWeight: chatbotAppearance?.["cuvo-chatbot-user-text-font-weight"],
    fontStyle: chatbotAppearance?.["cuvo-chatbot-user-text-font-style"],
    fontSize: chatbotAppearance?.["cuvo-chatbot-user-text-font-size"],
  };

  const botFontSettings = {
    color: chatbotAppearance?.["cuvo-chatbot-bot-text-color"],
    fontFamily: chatbotAppearance?.["cuvo-chatbot-bot-text-font-family"],
    fontWeight: chatbotAppearance?.["cuvo-chatbot-bot-text-font-weight"],
    fontStyle: chatbotAppearance?.["cuvo-chatbot-bot-text-font-style"],
    fontSize: chatbotAppearance?.["cuvo-chatbot-bot-text-font-size"],
  };

  const inputFontSettings = {
    color: chatbotAppearance?.["cuvo-chatbot-input-text-color"],
    fontFamily: chatbotAppearance?.["cuvo-chatbot-input-text-font-family"],
    fontWeight: chatbotAppearance?.["cuvo-chatbot-input-text-font-weight"],
    fontStyle: chatbotAppearance?.["cuvo-chatbot-input-text-font-style"],
    fontSize: chatbotAppearance?.["cuvo-chatbot-input-text-font-size"],
  };

  const newChatbotIcon = useMemo(() => {
    if (chatbotSettings.chatbotIcon) {
      const parser = new DOMParser();
      const svgDocument = parser.parseFromString(
        chatbotSettings.chatbotIcon,
        "image/svg+xml"
      );

      const pathElements = svgDocument.querySelectorAll("path");
      for (const pathElement of pathElements) {
        const fillAttribute = pathElement.getAttribute("fill");
        if (fillAttribute && fillAttribute !== "none") {
          pathElement.setAttribute(
            "fill",
            chatbotAppearance["cuvo-chatbot-bot-icon-fg-color"]
          );
        }
      }

      return new XMLSerializer().serializeToString(svgDocument);
    }
  }, [
    chatbotSettings.chatbotIcon,
    chatbotAppearance["cuvo-chatbot-bot-icon-fg-color"],
  ]);

  const newSendIcon = useMemo(() => {
    if (chatbotSettings.sendIcon) {
      const parser = new DOMParser();
      const svgDocument = parser.parseFromString(
        chatbotSettings.sendIcon,
        "image/svg+xml"
      );

      const pathElements = svgDocument.querySelectorAll("path");
      for (const pathElement of pathElements) {
        const fillAttribute = pathElement.getAttribute("fill");
        if (fillAttribute && fillAttribute !== "none") {
          pathElement.setAttribute(
            "fill",
            chatbotAppearance["cuvo-chatbot-send-icon-fg-color"]
          );
        }
      }

      return new XMLSerializer().serializeToString(svgDocument);
    }
  }, [
    chatbotSettings.sendIcon,
    chatbotAppearance["cuvo-chatbot-send-icon-fg-color"],
  ]);

  const [svgContent, setSvgContent] = useState<any>(null);

  useEffect(() => {
    const fetchSvg = async () => {
      const imgPath = chatbotSettings.sendIcon ? chatbotSettings.sendIcon : "";
      // "https://dashboard-cuvo-upload-photos.s3.amazonaws.com/product_6d414070-8396-11ee-a0ed-f3e6d67f790f/Demo1/logo.png";
      const fileExtension = getFileExtension(imgPath);
      if (fileExtension != "svg") {
        setSvgContent(chatbotSettings.sendIcon ? chatbotSettings.sendIcon : "");
      } else {
        try {
          const response = await fetch(
            chatbotSettings.sendIcon ? chatbotSettings.sendIcon : ""
          );

          if (!response.ok) {
            throw new Error(`Failed to fetch SVG. Status: ${response.status}`);
          }
          const svgText = await response.text();
          setSvgContent(`data:image/svg+xml;base64,${btoa(svgText)}`);
        } catch (error) {
          // console.error("Error fetching SVG:", error);
        }
      }
    };

    fetchSvg();
  }, [chatbotSettings]);
  const getFileExtension = (url: any) => {
    const parts = url.split(".");
    if (parts.length > 1) {
      return parts[parts.length - 1];
      // eslint-disable-next-line @typescript-eslint/tslint/config
    } else {
      return null;
    }
  };

  return (
    <Grid
      container
      sx={{
        padding: "0 10px",
        background: chatbotAppearance?.["cuvo-chatbot-body-bg-color"],
      }}
    >
      <Grid item xs={12} mt="25px" />
      <Grid container item xs={12} alignItems="center" columnGap="2px">
        <Box
          sx={{
            width: "38px",
            height: "38px",
            // background: chatbotAppearance?.["cuvo-chatbot-bot-icon-bg-color"],
            borderRadius: "50%",
            display: "grid",
            placeContent: "center",
          }}
        >
          <ChatbotIcon
            chatbotSettings={chatbotSettings}
            chatbotAppearance={chatbotAppearance}
          />
        </Box>

        <Box>
          <Box
            sx={{
              background: chatbotAppearance?.["cuvo-chatbot-bot-bg-color"],
              borderRadius: "8px",
              textAlign: "center",
              padding: "10px 14px",
            }}
          >
            <Typography
              sx={{
                ...botFontSettings,
              }}
            >
              {chatbotSettings.welcomeMsg}
            </Typography>
          </Box>
        </Box>
      </Grid>

      <Grid container item xs={12} justifyContent="end" mt="24px">
        <Grid item xs={8}>
          <Box
            sx={{
              background: chatbotAppearance?.["cuvo-chatbot-user-bg-color"],
              borderRadius: "8px",
              textAlign: "center",
              padding: "10px 14px",
            }}
          >
            <Typography
              sx={{
                ...userFontSettings,
              }}
            >
              How do I contact human support
            </Typography>
          </Box>
        </Grid>
        <Grid container item xs={1.5} justifyContent="end">
          <Avatar
            sx={{ width: "30px", height: "30px" }}
            src="https://source.unsplash.com/user/c_v_r/500x500"
          />
        </Grid>
      </Grid>

      <Grid container item xs={12} mt="24px">
        <Grid container item xs={1.5}>
          <Box
            sx={{
              width: "38px",
              height: "38px",
              // background: chatbotAppearance?.["cuvo-chatbot-bot-icon-bg-color"],
              borderRadius: "50%",
              display: "grid",
              placeContent: "center",
            }}
          >
            <ChatbotIcon
              chatbotSettings={chatbotSettings}
              chatbotAppearance={chatbotAppearance}
            />
          </Box>
        </Grid>
        <Grid item xs={9}>
          <Box
            sx={{
              background: chatbotAppearance?.["cuvo-chatbot-bot-bg-color"],
              borderRadius: "8px",
              padding: "10px 14px",
            }}
          >
            <Typography
              sx={{
                ...botFontSettings,
              }}
            >
              You can contact our support by clicking on the link
              https://www.yourdomain.com/{" "}
              {/* <Link
                href="#"
                sx={{
                  color:
                    chatbotAppearance &&
                    chatbotAppearance["cuvo-chatbot-bot-action-text-color"],
                }}
              >
                {" "}
                https://www.yourdomain.com/{" "}
              </Link>{" "} */}
              or by raising a ticket
            </Typography>
          </Box>
          <Typography sx={{ m: 1 }}>
            <svg
              width="20px"
              height="20px"
              viewBox="0 0 24 24"
              fill="#a9a8a8"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20.1818 9.36364H14.4455L15.3091 5.20909L15.3364 4.91818C15.3364 4.54545 15.1818 4.2 14.9364 3.95455L13.9727 3L7.99091 8.99091C7.65455 9.31818 7.45455 9.77273 7.45455 10.2727V19.3636C7.45455 20.3636 8.27273 21.1818 9.27273 21.1818H17.4545C18.2091 21.1818 18.8545 20.7273 19.1273 20.0727L21.8727 13.6636C21.9545 13.4545 22 13.2364 22 13V11.1818C22 10.1818 21.1818 9.36364 20.1818 9.36364Z"
                fill="#a9a8a8"
              />
              <path
                d="M5.63636 10.2727H2V21.1818H5.63636V10.2727Z"
                fill="#a9a8a8"
              />
            </svg>
            &nbsp;
            <svg
              width="20px"
              height="20px"
              viewBox="0 0 24 24"
              fill="#a9a8a8"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M14.7273 3H6.54545C5.79091 3 5.14545 3.45455 4.87273 4.10909L2.12727 10.5182C2.04545 10.7273 2 10.9455 2 11.1818V13C2 14 2.81818 14.8182 3.81818 14.8182H9.55455L8.69091 18.9727L8.66364 19.2636C8.66364 19.6364 8.81818 19.9818 9.06364 20.2273L10.0273 21.1818L16.0091 15.1909C16.3455 14.8636 16.5455 14.4091 16.5455 13.9091V4.81818C16.5455 3.81818 15.7273 3 14.7273 3ZM14.7273 13.9091L10.7818 17.8545L11.7909 13H3.81818V11.1818L6.54545 4.81818H14.7273V13.9091ZM22 3H18.3636V13.9091H22V3Z"
                fill="#a9a8a8"
              />
            </svg>
          </Typography>
        </Grid>
      </Grid>

      <Grid
        container
        item
        xs={12}
        justifyContent="center"
        alignItems="center"
        mt="24px"
        columnGap="15px"
      >
        <Button
          variant="contained"
          sx={{
            ":hover": {
              boxShadow: "none",
              bgcolor:
                chatbotAppearance &&
                chatbotAppearance["cuvo-chatbot-bot-action-bg-color"],
            },
            background:
              chatbotAppearance &&
              chatbotAppearance["cuvo-chatbot-bot-action-bg-color"],
            boxShadow: 0,
            color:
              chatbotAppearance &&
              chatbotAppearance["cuvo-chatbot-bot-action-text-color"],
            fontSize: "12px",
            textTransform: "unset",
          }}
        >
          Yes, Raise a ticket
        </Button>
        <Button
          variant="contained"
          sx={{
            ":hover": {
              boxShadow: "none",
              bgcolor:
                chatbotAppearance &&
                chatbotAppearance["cuvo-chatbot-bot-action-bg-color"],
            },
            background:
              chatbotAppearance &&
              chatbotAppearance["cuvo-chatbot-bot-action-bg-color"],
            boxShadow: 0,
            color:
              chatbotAppearance &&
              chatbotAppearance["cuvo-chatbot-bot-action-text-color"],
            fontSize: "12px",
            textTransform: "unset",
          }}
        >
          No, Thank you
        </Button>
      </Grid>

      <Grid
        container
        item
        xs={12}
        justifyContent="end"
        mt="24px"
        display="none"
      >
        <Grid item xs={8}>
          <Box
            sx={{
              background: chatbotAppearance?.["cuvo-chatbot-user-bg-color"],
              borderRadius: "8px",
              textAlign: "right",
              padding: "10px 14px",
            }}
          >
            <Typography
              sx={{
                ...userFontSettings,
              }}
            >
              Thank you that was helpful
            </Typography>
          </Box>
        </Grid>
        <Grid container item xs={2} justifyContent="end">
          <Avatar src="https://source.unsplash.com/user/c_v_r/500x500" />
        </Grid>
      </Grid>

      <Grid container item xs={12} mt="24px" alignItems="center">
        <Grid container item xs={2}>
          <Box
            sx={{
              width: "38px",
              height: "38px",
              // background: chatbotAppearance?.["cuvo-chatbot-bot-icon-bg-color"],
              borderRadius: "50%",
              display: "grid",
              placeContent: "center",
            }}
          >
            <ChatbotIcon
              chatbotSettings={chatbotSettings}
              chatbotAppearance={chatbotAppearance}
            />
          </Box>
        </Grid>
        <Grid container item xs={8}>
          {loadingPercentage.map((percent) => {
            return (
              <CircleIcon
                sx={{
                  color:
                    chatbotAppearance &&
                    chatbotAppearance["cuvo-chatbot-bot-bg-color"],
                  fontSize: "10px",
                  opacity: percent,
                }}
              />
            );
          })}
        </Grid>
      </Grid>

      {/* <Suggestions chatbotAppearance={chatbotAppearance} /> */}

      <Grid container item xs={12} mt="20px">
        <TextField
          className="chat-prev-input-user"
          variant="outlined"
          fullWidth
          sx={{
            background: chatbotAppearance?.["cuvo-chatbot-input-bg-color"],
            // borderColor: chatbotAppearance?.["cuvo-chatbot-input-text-color"],
            ...inputFontSettings,
            "& .MuiOutlinedInput-root:hover": {
              "& > fieldset": {
                borderColor: "#ccc",
              },
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  sx={{
                    width: "30px",
                    height: "30px",
                    background: "none",
                    // chatbotAppearance?.["cuvo-chatbot-send-icon-bg-color"],
                    borderRadius: "50%",
                    display: "grid",
                    placeContent: "center",
                    "&:hover": {
                      background: "none",
                      // chatbotAppearance?.["cuvo-chatbot-send-icon-bg-color"],
                    },
                  }}
                >
                  {!chatbotSettings.sendIcon && (
                    <SendIcon
                      color="#2858D3"
                      // color={
                      //   chatbotAppearance?.["cuvo-chatbot-send-icon-fg-color"]
                      // }
                    />
                  )}
                  {chatbotSettings.sendIcon && (
                    // <img
                    //   src={`data:image/svg+xml;utf8,${encodeURIComponent(
                    //     newSendIcon!
                    //   )}`}
                    //   alt="Send Icon"
                    // />
                    <img
                      style={{ width: "60px" }}
                      // src={chatbotSettings.sendIcon}
                      src={svgContent}
                    />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      <Grid container item xs={12} justifyContent="center" mt="18px" mb="8px">
        <Box
          sx={{
            // borderRadius: "8px",
            // background: "#000",
            height: "5px",
            width: "150px",
          }}
        />
      </Grid>
    </Grid>
  );
};

export default ChatbotBody;
